// AssessmentPortal — PUBLIC, token-only, read-only portal for external/SAC assessors. Rendered OUTSIDE the app
// shell. Styled with AuditPM's own design tokens (.card/.badge/.field + CSS variables) so it looks native.
// One scrolling page with a floating left nav that jumps to each section; management-system registers render
// inline (fully expanded — no click-through), Internal Audit / Management Review render their full records
// inline, and each sampled client file opens its full lifecycle pack. All data comes from SECURITY DEFINER
// RPCs; evidence files are signed by the assessment-file edge function after authorisation.
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { IA_CHECKLIST, IA_SECTIONS } from '../lib/internalAuditChecklist'
import { format, parseISO } from 'date-fns'
import { toast, failed } from '../lib/toast'

function fmt(d) { try { return d ? format(parseISO(d), 'd MMM yyyy') : '—' } catch { return '—' } }
function fmtT(d) { try { return d ? format(parseISO(d), 'd MMM yyyy, HH:mm') : '—' } catch { return '—' } }
function money(n) { return (n == null || n === '') ? '—' : 'S$' + Number(n).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
function humanize(k) { return String(k).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }

// The nine ConSASS records, in the order SAC expects them. Must match AssessmentAccess.js slot keys.
const CONSASS_DOC_TYPES = [
  ['application_form',       'Application form'],
  ['signed_quotation',       'Signed quotation'],
  ['audit_plan',             'Audit plan'],
  ['auditor_declarations',   "Auditors' declarations"],
  ['confirmation_email',     'Audit confirmation email'],
  ['report_review_approval', 'Audit report review & approval'],
  ['audit_report',           'Audit report'],
  ['attendance_record',      'Attendance record'],
  ['mom_submission',         'Successful submission of report (MOM)'],
]

// Audit date (or range) shown for a ConSASS/SHMS sample, and how many of the nine records were provided.
const CONSASS_KEYS = new Set(CONSASS_DOC_TYPES.map(([k]) => k))
function sampleDateStr(sm) {
  const dates = [sm.audit_start_date, sm.audit_end_date].filter(Boolean).map(fmt)
  if (dates.length === 2) return dates[0] === dates[1] ? dates[0] : `${dates[0]} – ${dates[1]}`
  return dates[0] || ''
}
// Named "Other documents" SAC asked for beyond the nine fixed records (doc_type 'other').
function sampleOthers(sm) {
  return (sm.files || []).filter(f => f.doc_type === 'other')
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
}
function sampleProvided(sm) {
  return new Set((sm.files || []).map(f => f.doc_type).filter(t => CONSASS_KEYS.has(t))).size
}

function labelEvidence(t) {
  const map = { rcm_document: 'Implementation document', evidence_pack: 'Audit evidence pack', audit_working_document: 'Audit working document', manual_checklist: 'Manual checklist (PDF)', manual_interview: 'Interview personnel sheet (PDF)' }
  if (map[t]) return map[t]
  if ((t || '').startsWith('rcm:')) return 'Implementation photo'
  return t || 'File'
}
function groupParticipants(list) {
  const map = new Map()
  for (const p of list || []) {
    const key = `${p.name || ''}|${p.designation || ''}`
    const e = map.get(key) || { name: p.name, designation: p.designation, opening: false, closing: false }
    if (p.present_opening || p.meeting === 'opening') e.opening = true
    if (p.present_closing || p.meeting === 'closing') e.closing = true
    map.set(key, e)
  }
  return Array.from(map.values())
}

// Response → AuditPM badge class (mirrors the internal-audit / report colour scheme).
function respBadge(r) {
  if (!r) return <span className="badge badge-gray">— to sight</span>
  const s = String(r).toLowerCase()
  const cls = (s.includes('complies') || s.includes('conform')) && !s.includes('non') ? 'badge-ok'
    : (s.includes('nonconform') || s === 'nc') ? 'badge-danger'
    : s.includes('observation') ? 'badge-warn'
    : s.includes('n/a') ? 'badge-gray'
    : 'badge-info'
  return <span className={`badge ${cls}`}>{r}</span>
}
function statusBadge(s) {
  const v = String(s || '').toLowerCase()
  const cls = v === 'closed' || v === 'held' ? 'badge-ok' : v === 'open' || v === 'draft' ? 'badge-warn' : 'badge-gray'
  return <span className={`badge ${cls}`}>{s || '—'}</span>
}

// ── Small presentational helpers (all use AuditPM tokens/classes) ──────────────────────────────────────────
function Card({ id, title, actions, children, navRef }) {
  return (
    <section id={id} ref={navRef} className="card" style={{ marginBottom: 16, scrollMarginTop: 16 }}>
      {(title || actions) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          {actions}
        </div>
      )}
      {children}
    </section>
  )
}
function KV({ k, v }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '170px minmax(0,1fr)', gap: 10, padding: '3px 0', fontSize: 13 }}>
    <div style={{ color: 'var(--text2)' }}>{k}</div><div style={{ minWidth: 0 }}>{v ?? '—'}</div>
  </div>
}
// Collapsible section: a clickable header bar, and its content only when open. Keeps the long
// management-system registers minimised by default so the assessor can jump straight to what they want.
function Section({ id, title, count, open, onToggle, children }) {
  return (
    <div id={id} style={{ marginBottom: 12, scrollMarginTop: 16 }}>
      <button onClick={onToggle} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        cursor: 'pointer', padding: '13px 16px', marginBottom: open ? 12 : 0, textAlign: 'left', border: '0.5px solid var(--border)' }}>
        <span aria-hidden="true" style={{ width: 12, color: 'var(--text3)', fontSize: 12 }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>{title}{count != null ? ` (${count})` : ''}</span>
        <span style={{ color: 'var(--brand)', fontSize: 12, fontWeight: 500 }}>{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  )
}
function Empty({ children }) { return <div style={{ fontSize: 12.5, color: 'var(--text3)', padding: '4px 0' }}>{children}</div> }

function printQuotation(q, client) {
  const esc = s => (s == null ? '' : String(s)).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  const row = (k, v) => `<tr><td style="width:230px;color:#555;font-weight:600">${esc(k)}</td><td>${v}</td></tr>`
  const remarks = `• Ensure your company is NOT SgMA certified by SCAL<br>• Confirm preferred audit date at least 1 month in advance<br>• For Level 3 or 4 — Level 1 and 2 certificate holders must be present on audit day${q.remarks ? '<br>• ' + esc(q.remarks) : ''}`
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(q.quote_ref || 'Quotation')}</title>
    <style>body{font-family:Arial,Helvetica,sans-serif;color:#222;margin:28px;font-size:12.5px}h1{font-size:18px;margin:0 0 2px}h2{font-size:13px;border-bottom:1px solid #999;padding-bottom:3px;margin:16px 0 8px}table{width:100%;border-collapse:collapse}td{border-bottom:0.5px solid #eee;padding:5px 7px;vertical-align:top}</style></head><body>
    <h1>Signed Proposal / Quotation</h1><div style="color:#666">${esc(q.quote_ref || '')} · CCIS Singapore Pte Ltd</div>
    <h2>Quotation</h2><table>
    ${row('Client', esc(client))}${row('Audit type', esc(q.audit_type))}${row('Audit scope', esc(q.scope))}
    ${row('Man-days', esc(q.mandays))}${row('Validity', (q.validity_days || 30) + ' days')}${row('Indicative timeline', esc(q.timeline))}
    ${row('Quoted fee', money(q.fee_ex_gst) + ' excl. GST &nbsp;/&nbsp; ' + money(q.fee_inc_gst) + ' incl. 9% GST')}
    ${row('Payment terms', '100% upfront payment · PayNow / GIRO preferred (UEN 199205611D)')}${row('Remarks', remarks)}</table>
    <h2>Acceptance / Signature</h2><table>
    ${row('Status', esc(q.status))}${row('Signed date', esc(q.signed_date))}${row('Accepted by', esc(q.signatory_name || q.application_accepted_by))}
    ${row('Designation', esc(q.signatory_designation))}${row('Application accepted at', esc(q.application_accepted_at))}${row('Signed via secure link', q.signed_via_token ? 'Yes' : 'No')}</table>
    </body></html>`
  const w = window.open('', '_blank'); if (!w) { toast('Allow pop-ups to view or print', 'warning'); return }
  w.document.write(html); w.document.close(); w.focus()
}

// Impartiality register as its own read-only page (not an inline accordion) — the register is wide (12
// columns) and burying it mid-scroll on the index page made it unusable for assessors. Mirrors the
// JobPack/SamplePack "click through, then back" pattern used elsewhere in this portal.
function ImpartialityPack({ impartiality, onBack }) {
  return (
    <div style={{ maxWidth: 1600, width: '100%', margin: '0 auto' }}>
      <button className="sm" style={{ marginBottom: 12 }} onClick={onBack}>← Back to assessment</button>
      <Card title="Impartiality register" actions={<span style={{ fontSize: 12, color: 'var(--text2)' }}>{impartiality.length} {impartiality.length === 1 ? 'entry' : 'entries'}</span>}>
        {impartiality.length === 0 ? <Empty>No entries.</Empty> : (
          <div className="table-wrap"><table style={{ width: '100%', tableLayout: 'auto' }}>
            <thead><tr><th>ID</th><th>Description</th><th>Category</th><th>Affected area</th><th>L</th><th>I</th><th>Level</th><th>Owner</th><th>Mitigation</th><th>Residual</th><th>Reviewed by</th><th>Last reviewed</th></tr></thead>
            <tbody>{impartiality.map((r, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 500 }}>{r.risk_id || '—'}</td>
                <td style={{ minWidth: 220 }}>{r.risk_description || '—'}</td>
                <td style={{ fontSize: 12 }}>{r.risk_category || '—'}</td>
                <td style={{ fontSize: 12 }}>{r.affected_area || '—'}</td>
                <td style={{ fontSize: 12 }}>{r.likelihood || '—'}</td>
                <td style={{ fontSize: 12 }}>{r.impact || '—'}</td>
                <td style={{ fontSize: 12 }}>{r.risk_level || '—'}</td>
                <td style={{ fontSize: 12 }}>{r.owner || '—'}</td>
                <td style={{ minWidth: 200, fontSize: 12 }}>{r.mitigation || '—'}</td>
                <td style={{ minWidth: 160, fontSize: 12 }}>{r.residual_risk || '—'}</td>
                <td style={{ fontSize: 12 }}>{r.revised_by || r.reviewed_by || '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{fmt(r.last_reviewed_date)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  )
}

export default function AssessmentPortal() {
  const { token } = useParams()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [entered, setEntered] = useState(false)
  const [portal, setPortal] = useState(null)
  const [loading, setLoading] = useState(false)
  const [iaFull, setIaFull] = useState({})   // internal_audit_id -> full record
  const [mrFull, setMrFull] = useState({})   // management_review_id -> full record
  const [job, setJob] = useState(null)       // open job pack { data, media, mediaById }
  const [sample, setSample] = useState(null)  // open ConSASS/SHMS sample pack (one company's nine records)
  const [showImpartiality, setShowImpartiality] = useState(false)  // open Impartiality register as its own page

  // Remember the assessor's self-declared identity for this browsing session, so a refresh / back / idle
  // re-opens the portal instead of forcing re-entry. Cleared when the browser session ends.
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('assess_' + token) || 'null')
      if (saved && saved.name) { setName(saved.name); setEmail(saved.email || ''); enter(saved.name, saved.email || '') }
    } catch (err) {}
  }, [token]) // eslint-disable-line

  async function enter(n, e) {
    const useName = (n ?? name) || '', useEmail = (e ?? email) || ''
    setLoading(true)
    const { data } = await supabase.rpc('get_assessment_portal', { p_token: token, p_name: useName || null, p_email: useEmail || null })
    const p = data || { ok: false, error: 'Could not load the assessment portal.' }
    setPortal(p); setEntered(true)
    if (p.ok) {
      try { sessionStorage.setItem('assess_' + token, JSON.stringify({ name: useName, email: useEmail })) } catch (err) {}
      const iaMap = {}, mrMap = {}
      await Promise.all([
        ...(p.internal_audits || []).map(async ia => { const { data: d } = await supabase.rpc('get_assessment_internal_audit', { p_token: token, p_id: ia.id, p_name: useName || null, p_email: useEmail || null }); if (d?.ok) iaMap[ia.id] = d }),
        ...(p.management_reviews || []).map(async mr => { const { data: d } = await supabase.rpc('get_assessment_management_review', { p_token: token, p_id: mr.id, p_name: useName || null, p_email: useEmail || null }); if (d?.ok) mrMap[mr.id] = d }),
      ])
      setIaFull(iaMap); setMrFull(mrMap)
    }
    setLoading(false)
  }
  async function signFile(kind, id, download) {
    const { data, error } = await supabase.functions.invoke('assessment-file', { body: { token, kind, id, name: name || null, email: email || null, download: !!download } })
    if (!error && data?.url) return data.url
    // Report the REAL reason. The old blanket "the edge function must be deployed" message was a guess, and
    // it hid the actual cause twice (Sep 2026): the function was deployed, it was refusing the file kind.
    let detail = data?.error || ''
    try { if (error?.context?.json) detail = (await error.context.json())?.error || detail } catch (e) {}
    if (!detail) detail = error?.message || 'no response from the file service'
    failed("Couldn't open the file", detail)
    return null
  }
  async function openFile(kind, id, download) { const u = await signFile(kind, id, download); if (u) window.open(u, '_blank', 'noopener') }

  async function openJob(job_id) {
    setLoading(true)
    const { data, error } = await supabase.rpc('get_assessment_job', { p_token: token, p_job_id: job_id, p_name: name || null, p_email: email || null })
    if (error) { setLoading(false); failed("Couldn't open the audit", error.message); return }
    if (!data?.ok) { setLoading(false); toast('This link is no longer available', 'info', { detail: data?.error }); return }
    const ev = data.report_doc?.snapshot?.snapshot?.evidence || []
    const media = {}
    await Promise.all(ev.filter(e => e.id && e.storage_path).map(async e => { const u = await signFile('evidence', e.id, false); if (u) media[e.storage_path] = u }))
    const mediaById = {}
    ev.forEach(e => { if (e.id && e.storage_path && media[e.storage_path]) mediaById[e.id] = media[e.storage_path] })
    // Documents office attached to this job for the assessment (signed quotation, anything SAC asks for).
    const { data: af } = await supabase.rpc('get_assessment_job_files', { p_token: token, p_job_id: job_id, p_name: name || null, p_email: email || null })
    const attachments = af?.ok ? (af.files || []) : []
    setJob({ data, media, mediaById, attachments, job_id }); window.scrollTo(0, 0); setLoading(false)
  }
  // Management-system sections start collapsed. Clicking a left-nav item (or its header) expands it.
  const MS_IDS = ['documents', 'personnel', 'internal-audit', 'management-review', 'complaints']
  const [openSections, setOpenSections] = useState({})
  const toggleSection = (id) => setOpenSections(o => ({ ...o, [id]: !o[id] }))
  const setAllSections = (val) => setOpenSections(Object.fromEntries(MS_IDS.map(k => [k, val])))
  function scrollTo(id) {
    if (MS_IDS.includes(id)) setOpenSections(o => ({ ...o, [id]: true }))   // expand before jumping
    setTimeout(() => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }) }, 60)
  }

  // ── Shell (brand header + centred body) ──────────────────────────────────────────────────────────────────
  const shell = (children, wide) => (
    <div style={{ minHeight: '100vh', background: 'var(--bg2)' }}>
      <div style={{ background: 'var(--brand-darker)', color: '#fff', padding: '14px 22px' }}>
        <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '0.3px' }}>CCIS Singapore Pte Ltd</div>
        <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)' }}>AuditPM · Assessment Portal</div>
      </div>
      <div style={{ maxWidth: wide === 'xwide' ? 1620 : wide ? 1120 : 760, margin: '0 auto', padding: '22px 16px' }}>{children}</div>
    </div>
  )

  // Gate (self-declared identity for the access log — not authentication)
  if (!entered) return shell(
    <div className="card">
      <h1 style={{ marginTop: 0 }}>External assessment access</h1>
      <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>Please enter your name and organisation email to open this read-only assessment portal. This is recorded in the access log.</p>
      <div className="field"><label>Your name</label><input value={name} onChange={e => setName(e.target.value)} /></div>
      <div className="field"><label>Email</label><input value={email} onChange={e => setEmail(e.target.value)} /></div>
      <button className="primary" onClick={() => enter()} disabled={loading || !name.trim()}>{loading ? 'Opening…' : 'Open assessment portal'}</button>
    </div>
  )
  if (loading && !portal) return shell(<div className="card">Loading…</div>)
  if (!portal?.ok) return shell(<div className="card" style={{ color: 'var(--danger)' }}>{portal?.error || 'This assessment link is not available.'}</div>)

  // ── Open job pack (full lifecycle file) ──────────────────────────────────────────────────────────────────
  if (job) return shell(<JobPack job={job} onBack={() => { setJob(null); window.scrollTo(0, 0) }} openFile={openFile} />, true)

  // ── Open ConSASS/SHMS sample pack (one company's nine records) ──────────────────────────────
  // No fetch needed: the portal payload already carries every sample and its files.
  if (sample) return shell(<SamplePack sample={sample} onBack={() => { setSample(null); window.scrollTo(0, 0) }} openFile={openFile} />, true)

  // ── Open Impartiality register as its own page — a click-through, not an inline accordion table.
  // The register can run to a dozen+ wide columns; expanding it in place made the assessor scroll a
  // page-wide table buried in the middle of everything else. A dedicated page (like JobPack/SamplePack)
  // reads far easier.
  if (showImpartiality) return shell(<ImpartialityPack impartiality={portal.impartiality_register || []} onBack={() => { setShowImpartiality(false); window.scrollTo(0, 0) }} />, 'xwide')

  // ── Index (single page + floating nav) ───────────────────────────────────────────────────────────────────
  const documents = portal.documents || []
  const jobs = portal.jobs || []
  const manualSamples = portal.manual_samples || []
  const internalAudits = portal.internal_audits || []
  const managementReviews = portal.management_reviews || []
  const impartiality = portal.impartiality_register
  const complaints = portal.complaints
  const personnel = portal.personnel

  const navClient = [
    { id: 'overview', label: 'Overview' },
    { id: 'jobs', label: `WSH RM Audit sampled files (${jobs.length})` },
    ...(manualSamples.length > 0 ? [{ id: 'consass', label: `ConSASS/SHMS sampled files (${manualSamples.length})` }] : []),
  ]
  const navMS = [
    { id: 'documents', label: 'M&P & documents', show: documents.length > 0 },
    { id: 'impartiality', label: 'Impartiality register', show: !!impartiality },
    { id: 'personnel', label: 'Personnel & competence', show: !!personnel },
    { id: 'internal-audit', label: 'Internal audit', show: internalAudits.length > 0 },
    { id: 'management-review', label: 'Management review', show: managementReviews.length > 0 },
    { id: 'complaints', label: 'Complaints', show: !!complaints },
  ].filter(x => x.show)
  const navBtn = { display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '8px 10px', fontSize: 12.5, color: 'var(--text)', borderRadius: 6 }
  const navHdr = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text3)', padding: '10px 8px 2px' }

  // Cheap, high-value cross-link: which released jobs each auditor worked (name match against released jobs).
  const jobsForAuditor = (nm) => jobs.filter(j => nm && (j.auditor === nm || j.auditor2 === nm)).map(j => j.client || j.label)

  return shell(
    <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start' }}>
      {/* Floating left nav */}
      <nav style={{ position: 'sticky', top: 16, flex: '0 0 210px', width: 210, alignSelf: 'flex-start' }}>
        <div className="card" style={{ padding: 10 }}>
          <div style={{ ...navHdr, padding: '4px 8px' }}>Contents</div>
          {navClient.map(n => (
            <button key={n.id} onClick={() => scrollTo(n.id)} style={navBtn}>{n.label}</button>
          ))}
          {navMS.length > 0 && <div style={navHdr}>Management system</div>}
          {navMS.map(n => (
            <button key={n.id} onClick={() => { if (n.id === 'impartiality') { setShowImpartiality(true); window.scrollTo(0, 0) } else scrollTo(n.id) }} style={navBtn}>{n.label}</button>
          ))}
        </div>
      </nav>

      {/* Main column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Card id="overview" title={portal.session?.name || 'Assessment access'}>
          <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 12 }}>
            {portal.session?.assessor_org ? `${portal.session.assessor_org} · ` : ''}Valid until {fmtT(portal.session?.valid_until)}
          </div>
          <div className="notice notice-warn" style={{ marginBottom: 0 }}>
            Read-only. You see only the records released for this assessment — use the contents list on the left to jump to a section. Session-attendance photographs are internal attendance evidence, not part of the client report.
          </div>
          <div className="stat-grid" style={{ marginTop: 14, marginBottom: 0 }}>
            <div className="stat-card"><div className="stat-val">{jobs.length}</div><div className="stat-lbl">WSH RM Audit</div></div>
            {manualSamples.length > 0 && <div className="stat-card"><div className="stat-val">{manualSamples.length}</div><div className="stat-lbl">ConSASS/SHMS</div></div>}
            <div className="stat-card"><div className="stat-val">{documents.length}</div><div className="stat-lbl">Documents</div></div>
            <div className="stat-card"><div className="stat-val">{internalAudits.length}</div><div className="stat-lbl">Internal audits</div></div>
            <div className="stat-card"><div className="stat-val">{managementReviews.length}</div><div className="stat-lbl">Management reviews</div></div>
          </div>
        </Card>

        <Card id="jobs" title={`WSH RM Audit sampled files (${jobs.length})`}>
          {jobs.length === 0 ? <Empty>None released.</Empty> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Client</th><th>Scheme</th><th>Audit date</th><th>Auditor(s)</th><th>State</th><th style={{ width: 80 }}></th></tr></thead>
              <tbody>{jobs.map(jj => (
                <tr key={jj.job_id}>
                  <td style={{ fontWeight: 500 }}>{jj.client || jj.label}</td>
                  <td style={{ fontSize: 12 }}>{jj.scope || jj.audit_type || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{fmt(jj.audit_date)}</td>
                  <td style={{ fontSize: 12 }}>{[jj.auditor, jj.auditor2].filter(Boolean).join(', ') || '—'}</td>
                  <td style={{ fontSize: 12 }}>{jj.workflow_state || '—'}</td>
                  <td><button className="sm primary" onClick={() => openJob(jj.job_id)}>Open</button></td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Card>

        {manualSamples.length > 0 && (
          <Card id="consass" title={`ConSASS/SHMS sampled files (${manualSamples.length})`}>
            <div className="table-wrap"><table>
              <thead><tr><th>Company</th><th>Project</th><th>Audit date</th><th>Records</th><th style={{ width: 80 }}></th></tr></thead>
              <tbody>{manualSamples.map(sm => (
                <tr key={sm.id}>
                  <td style={{ fontWeight: 500 }}>{sm.company_name}</td>
                  <td style={{ fontSize: 12 }}>{sm.project_name || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{sampleDateStr(sm) || '—'}</td>
                  <td style={{ fontSize: 12 }}>{sampleProvided(sm)} of {CONSASS_DOC_TYPES.length}{sampleOthers(sm).length > 0 ? ` · +${sampleOthers(sm).length} other` : ''}</td>
                  <td><button className="sm primary" onClick={() => { setSample(sm); window.scrollTo(0, 0) }}>Open</button></td>
                </tr>
              ))}</tbody>
            </table></div>
          </Card>
        )}

        {(documents.length > 0 || impartiality || personnel || internalAudits.length > 0 || managementReviews.length > 0 || complaints) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 2px 12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text3)' }}>Management system</div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="sm" onClick={() => setAllSections(true)}>Expand all</button>
              <button className="sm" onClick={() => setAllSections(false)}>Collapse all</button>
            </div>
          </div>
        )}

        {documents.length > 0 && (
          <Section id="documents" title="Management & Procedures Manual and controlled documents" count={documents.length} open={openSections['documents']} onToggle={() => toggleSection('documents')}>
            <div className="card">
              <div className="table-wrap"><table>
                <thead><tr><th>Code</th><th>Title</th><th>Type</th><th style={{ width: 90 }}></th></tr></thead>
                <tbody>{documents.map(dd => (
                  <tr key={dd.document_id}>
                    <td style={{ fontWeight: 500 }}>{dd.code || '—'}</td>
                    <td>{dd.title}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{dd.type}</td>
                    <td><DocumentOpen token={token} documentId={dd.document_id} name={name} email={email} openFile={openFile} /></td>
                  </tr>
                ))}</tbody>
              </table></div>
            </div>
          </Section>
        )}

        {impartiality && (
          <Card id="impartiality" title="Impartiality register" actions={
            <button className="sm primary" onClick={() => { setShowImpartiality(true); window.scrollTo(0, 0) }}>View</button>
          }>
            <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>
              {impartiality.length} risk {impartiality.length === 1 ? 'entry' : 'entries'} on record. Click <b>View</b> to open the full register.
            </div>
          </Card>
        )}

        {personnel && (
          <Section id="personnel" title="Personnel & competence" count={personnel.length} open={openSections['personnel']} onToggle={() => toggleSection('personnel')}>
            <div className="card">
            {personnel.length === 0 ? <Empty>No active personnel.</Empty> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Name</th><th>Role</th><th>Approval</th><th>WSHO licence</th><th>DM authorised</th><th>Competence review due</th><th>Sampled files</th></tr></thead>
                <tbody>{personnel.map((a, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{a.name}</td>
                    <td style={{ fontSize: 12 }}>{[a.is_auditor ? 'Auditor' : null, a.is_decision_maker ? 'Decision-maker' : null].filter(Boolean).join(' · ') || '—'}</td>
                    <td style={{ fontSize: 12 }}>{a.is_auditor ? (a.approval_status || '—') : 'N/A'}</td>
                    <td style={{ fontSize: 12 }}>{a.wsho_status ? fmt(a.wsho_licence_expiry) : '—'}</td>
                    <td style={{ fontSize: 12 }}>{a.is_decision_maker ? (a.dm_authorised ? `Yes${Array.isArray(a.dm_scope) && a.dm_scope.length ? ' · ' + a.dm_scope.join(', ') : ''}` : 'No') : '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{fmt(a.next_competence_review_due)}</td>
                    <td style={{ fontSize: 11.5, color: 'var(--text2)', minWidth: 140 }}>{jobsForAuditor(a.name).join(', ') || '—'}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
            </div>
          </Section>
        )}

        {internalAudits.length > 0 && (
          <Section id="internal-audit" title="Internal audit" count={internalAudits.length} open={openSections['internal-audit']} onToggle={() => toggleSection('internal-audit')}>
            {internalAudits.map(ia => <InternalAuditReport key={ia.id} meta={ia} full={iaFull[ia.id]} />)}
          </Section>
        )}

        {managementReviews.length > 0 && (
          <Section id="management-review" title="Management review" count={managementReviews.length} open={openSections['management-review']} onToggle={() => toggleSection('management-review')}>
            {managementReviews.map(mr => <ManagementReviewRecord key={mr.id} meta={mr} full={mrFull[mr.id]} />)}
          </Section>
        )}

        {complaints && (
          <Section id="complaints" title="Complaints" count={complaints.length} open={openSections['complaints']} onToggle={() => toggleSection('complaints')}>
            <div className="card">
            {complaints.length === 0 ? <Empty>No complaints recorded.</Empty> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Ref</th><th>Date</th><th>Client</th><th>Subject</th><th>Status</th><th>Outcome</th><th>Handled by</th><th>Reviewed by (independent)</th><th>Closed</th></tr></thead>
                <tbody>{complaints.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{c.complaint_ref || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{fmt(c.complaint_date)}</td>
                    <td style={{ fontSize: 12 }}>{c.client || '—'}</td>
                    <td style={{ fontSize: 12, minWidth: 180 }}>{c.subject || '—'}</td>
                    <td>{statusBadge(c.status)}</td>
                    <td style={{ fontSize: 12, minWidth: 160 }}>{c.outcome || '—'}</td>
                    <td style={{ fontSize: 12 }}>{c.handled_by || '—'}</td>
                    <td style={{ fontSize: 12 }}>{c.reviewed_by || '—'}{c.independence_note ? <div style={{ color: 'var(--text3)', fontSize: 11 }}>{c.independence_note}</div> : null}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{fmt(c.closed_date)}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
            </div>
          </Section>
        )}

      </div>
    </div>,
    true
  )
}

// ── Controlled-document opener (fetches revisions on demand, then signs the file) ──────────────────────────
function DocumentOpen({ token, documentId, name, email, openFile }) {
  const [revs, setRevs] = useState(null)
  const [busy, setBusy] = useState(false)
  async function load() {
    setBusy(true)
    const { data } = await supabase.rpc('get_assessment_document', { p_token: token, p_document_id: documentId, p_name: name || null, p_email: email || null })
    setBusy(false)
    if (!data?.ok) { toast('This document is no longer available', 'info', { detail: data?.error }); return }
    setRevs(data.revisions || [])
  }
  if (revs) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {revs.length === 0 ? <span style={{ fontSize: 11, color: 'var(--text3)' }}>No files</span> : revs.map(r => (
        <button key={r.revision_id} className="sm" onClick={() => openFile('revision', r.revision_id, false)} title={`Rev ${r.revision_no} · ${r.status}`}>View rev {r.revision_no}</button>
      ))}
    </div>
  )
  return <button className="sm" disabled={busy} onClick={load}>{busy ? '…' : 'Open'}</button>
}

// ── Internal-audit full report (read-only, whole checklist expanded) ───────────────────────────────────────
function InternalAuditReport({ meta, full }) {
  if (!full) return <Card title={`Internal audit — ${meta.ref}`}><Empty>Loading…</Empty></Card>
  const a = full.audit || {}
  const cl = a.checklist || {}
  const samples = full.samples || []
  const findings = full.findings || []
  const clResp = it => (cl[it.id]?.r || it.r || '')
  const clEvid = it => (cl[it.id]?.e ?? it.e ?? '')
  const answered = IA_CHECKLIST.filter(it => clResp(it)).length
  return (
    <Card title={`Internal audit — ${a.ref || meta.ref}${a.title ? ' · ' + a.title : ''}`} actions={statusBadge(a.status)}>
      <div style={{ marginBottom: 12 }}>
        <KV k="Objective" v={a.objective} />
        <KV k="Scope" v={a.scope} />
        <KV k="Criteria" v={a.criteria} />
        <KV k="Lead internal auditor" v={a.lead_internal_auditor} />
        <KV k="Audit period" v={`${fmt(a.period_start)} – ${fmt(a.period_end)}`} />
        <KV k="Planned / conducted" v={`${fmt(a.planned_date)} / ${fmt(a.conducted_date)}`} />
      </div>

      <h3 style={{ margin: '14px 0 6px' }}>Samples ({samples.length})</h3>
      {samples.length === 0 ? <Empty>No samples.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Reference</th><th>Scheme</th><th>Audit date</th><th>Conformity</th><th>Notes</th></tr></thead>
          <tbody>{samples.map((s, i) => { const sn = s.snapshot || {}; return (
            <tr key={i}><td style={{ fontSize: 12 }}>{s.reference_label}</td><td style={{ fontSize: 12 }}>{sn.audit_type || '—'}</td>
              <td style={{ fontSize: 12, color: 'var(--text2)' }}>{sn.audit_date || '—'}</td>
              <td>{s.conformity ? respBadge(s.conformity) : '—'}</td><td style={{ fontSize: 12 }}>{s.sample_notes || '—'}</td></tr>
          ) })}</tbody>
        </table></div>
      )}

      <h3 style={{ margin: '16px 0 6px' }}>Findings ({findings.length})</h3>
      {findings.length === 0 ? <Empty>No findings recorded.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Type</th><th>Standard / clause</th><th>Description</th><th>Evidence</th><th>Status</th></tr></thead>
          <tbody>{findings.map((f, i) => (
            <tr key={i}><td>{respBadge(f.finding_type)}</td><td style={{ fontSize: 12 }}>{f.standard || ''} {f.clause_ref || ''}</td>
              <td style={{ fontSize: 12, minWidth: 200 }}>{f.description || '—'}</td><td style={{ fontSize: 12 }}>{f.evidence || '—'}</td>
              <td>{statusBadge(f.status)}</td></tr>
          ))}</tbody>
        </table></div>
      )}

      <h3 style={{ margin: '16px 0 6px' }}>Checklist — SAC / CT 17 ({answered} of {IA_CHECKLIST.length} answered)</h3>
      <div className="table-wrap"><table>
        <thead><tr><th style={{ width: 70 }}>Clause</th><th>Requirement</th><th style={{ width: 130 }}>Response</th><th>Evidence</th></tr></thead>
        <tbody>
          {Object.entries(IA_SECTIONS).map(([k, label]) => {
            const items = IA_CHECKLIST.filter(it => it.s === k)
            if (!items.length) return null
            return (
              <React.Fragment key={k}>
                <tr><td colSpan={4} style={{ background: 'var(--bg3)', color: 'var(--brand-dark)', fontWeight: 600, fontSize: 12 }}>{label}</td></tr>
                {items.map(it => (
                  <tr key={it.id}>
                    <td style={{ fontSize: 11, whiteSpace: 'nowrap', color: 'var(--text2)' }}>{it.cl}</td>
                    <td style={{ fontSize: 12 }}>{it.q}</td>
                    <td>{respBadge(clResp(it))}</td>
                    <td style={{ fontSize: 11.5, color: 'var(--text2)', minWidth: 180 }}>{clEvid(it) || '—'}</td>
                  </tr>
                ))}
              </React.Fragment>
            )
          })}
        </tbody>
      </table></div>

      <h3 style={{ margin: '16px 0 6px' }}>Conclusion</h3>
      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{a.conclusion || '—'}</div>
    </Card>
  )
}

// ── Management-review full record (read-only) ──────────────────────────────────────────────────────────────
function ManagementReviewRecord({ meta, full }) {
  if (!full) return <Card title={`Management review — ${meta.ref}`}><Empty>Loading…</Empty></Card>
  const r = full.review || {}
  const inputs = full.inputs || []
  const actions = full.actions || []
  return (
    <Card title={`Management review — ${r.ref || meta.ref}`} actions={statusBadge(r.status)}>
      <div style={{ marginBottom: 12 }}>
        <KV k="Review date" v={fmt(r.review_date)} />
        <KV k="Period covered" v={`${fmt(r.period_start)} – ${fmt(r.period_end)}`} />
        <KV k="Chair" v={r.chair} />
        <KV k="Attendees" v={r.attendees} />
      </div>

      <h3 style={{ margin: '14px 0 6px' }}>Review inputs</h3>
      {inputs.length === 0 ? <Empty>No inputs recorded.</Empty> : inputs.map((inp, i) => (
        <div key={i} style={{ borderTop: '0.5px solid var(--border)', padding: '8px 0' }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>{inp.input_title || humanize(inp.input_key)}</div>
          {inp.auto_summary && typeof inp.auto_summary === 'object' && (
            <div style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', flexWrap: 'wrap', gap: '2px 16px', marginBottom: 4 }}>
              {Object.entries(inp.auto_summary).map(([k, v]) => <span key={k}><b style={{ color: 'var(--text)' }}>{v}</b> {humanize(k)}</span>)}
            </div>
          )}
          <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{inp.manual_notes || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
        </div>
      ))}

      <h3 style={{ margin: '16px 0 6px' }}>Decisions & action items ({actions.length})</h3>
      {actions.length === 0 ? <Empty>No action items.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Issue / decision</th><th>Action required</th><th>Owner</th><th>Due</th><th>Status</th></tr></thead>
          <tbody>{actions.map((c, i) => (
            <tr key={i}><td style={{ fontSize: 12, minWidth: 200 }}>{c.issue || '—'}</td><td style={{ fontSize: 12 }}>{c.action_required || '—'}</td>
              <td style={{ fontSize: 12 }}>{c.owner || '—'}</td><td style={{ fontSize: 12, color: 'var(--text2)' }}>{fmt(c.due_date)}</td><td>{statusBadge(c.status)}</td></tr>
          ))}</tbody>
        </table></div>
      )}

      {r.summary && <><h3 style={{ margin: '16px 0 6px' }}>Discussion / minutes</h3><div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{r.summary}</div></>}
    </Card>
  )
}

// ── Application (the client's submitted form) as its own read-only page — SAC calls this "Application",
// never "questionnaire", so that's the only word used here. Mirrors the ImpartialityPack click-through
// pattern rather than burying a long Q&A list inline in the job file.
// These four are optional on submission — but must show explicitly as "Nil" rather than being silently
// omitted, so the assessor sees every declaration item was addressed, not missing. Mirrors the same
// fix in Quotations.js's QuestionnaireView (the internal office view of the same questionnaire).
const APP_NIL_IF_EMPTY = ['outsourced_detail', 'consultancy_detail', 'management_system_changes_detail', 'key_personnel_changes_detail']

function ApplicationPack({ quotation, onBack }) {
  const r = quotation.form_responses || {}
  const isBlank = v => v == null || v === '' || (Array.isArray(v) && v.length === 0)
  const entries = Object.entries(r).filter(([k, v]) => !isBlank(v) || APP_NIL_IF_EMPTY.includes(k))
  // The field may be entirely absent from form_responses (never even reached as an empty string) —
  // still show it as Nil rather than dropping it.
  for (const k of APP_NIL_IF_EMPTY) if (!(k in r)) entries.push([k, null])
  const val = v => isBlank(v) ? 'Nil' : (Array.isArray(v) ? v.join(', ') : (typeof v === 'object' ? JSON.stringify(v) : String(v)))
  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <button className="sm" style={{ marginBottom: 12 }} onClick={onBack}>← Back to job</button>
      <Card title="Application" actions={<span style={{ fontSize: 12, color: 'var(--text2)' }}>{quotation.quote_ref}</span>}>
        <KV k="Submitted by" v={r.pic_name || '—'} />
        <KV k="Submitted on" v={fmtT(quotation.submitted_at)} />
        <div style={{ borderTop: '0.5px solid var(--border)', margin: '12px 0' }} />
        {entries.length === 0 ? <Empty>No application details on record.</Empty> :
          entries.map(([k, v]) => <KV key={k} k={humanize(k)} v={val(v)} />)}
      </Card>
    </div>
  )
}

// ── Job pack (full lifecycle client file) — restyled with AuditPM cards + anchor sub-nav ───────────────────
function JobPack({ job, onBack, openFile }) {
  const [showApplication, setShowApplication] = useState(false)
  const d = job.data
  const sa = d.session_attendance || {}
  const attachments = job.attachments || []
  const reportDoc = d.report_doc || null
  const hasReport = !!(reportDoc && reportDoc.snapshot)   // an actual submitted report exists (not an empty shell)
  const q = d.quotation || {}
  const pa = d.acknowledgements?.plan_ack || null
  const re = d.acknowledgements?.report_endorsement || null
  const dec = d.decision || {}
  const mediaById = job.mediaById || {}
  const parts = groupParticipants(d.participants)
  const evAll = (d.evidence || []).filter(e => e.type !== 'session_photo')
  const implPhotos = evAll.filter(e => (e.type || '').startsWith('rcm:'))
  const otherFiles = evAll.filter(e => !(e.type || '').startsWith('rcm:'))
  const sub = [
    ['jp-summary', 'Summary'], ['jp-app', 'Application'], ['jp-quote', 'Quotation'], ['jp-planack', 'Plan ack'], ['jp-coi', 'COI'],
    ['jp-meet', 'Attendance'], ['jp-exec', 'Execution & decision'], ['jp-endorse', 'Endorsement'],
    ['jp-wshc', 'WSHC'], hasReport ? ['jp-report', 'Report'] : null, ['jp-evidence', 'Evidence'], ['jp-trail', 'Audit trail'],
  ].filter(Boolean)
  const scrollTo = id => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }) }

  if (showApplication) return <ApplicationPack quotation={q} onBack={() => { setShowApplication(false); window.scrollTo(0, 0) }} />

  return (
    <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start' }}>
      <nav style={{ position: 'sticky', top: 16, flex: '0 0 190px', width: 190 }}>
        <div className="card" style={{ padding: 10 }}>
          <button className="sm" style={{ width: '100%', marginBottom: 6 }} onClick={onBack}>← Back to assessment</button>
          {sub.map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '7px 10px', fontSize: 12.5, color: 'var(--text)', borderRadius: 6 }}>{label}</button>
          ))}
        </div>
      </nav>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Card id="jp-summary" title="Job summary">
          <KV k="Client" v={d.summary?.client} /><KV k="Scope" v={d.summary?.scope} /><KV k="Audit type" v={d.summary?.audit_type} />
          <KV k="Audit date" v={fmt(d.summary?.audit_date)} /><KV k="Workflow state" v={d.summary?.workflow_state} />
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text3)', margin: '12px 0 6px' }}>File completeness</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[['Quotation', !!q.quote_ref], ['Plan ack', !!pa], ['COI', (d.coi || []).length > 0], ['Report', hasReport], ['Endorsement', !!re], ['Decision', !!dec.outcome], ['WSHC', !!(d.wshc?.submitted_by || d.wshc?.outcome)]].map(([label, ok]) => (
              <span key={label} className={`badge ${ok ? 'badge-ok' : 'badge-gray'}`}>{ok ? '✓' : '—'} {label}</span>
            ))}
          </div>
        </Card>

        {q.quote_ref && (
          <Card id="jp-app" title="Application" actions={<button className="sm" onClick={() => { setShowApplication(true); window.scrollTo(0, 0) }}>View Application</button>}>
            <KV k="Submitted by" v={q.form_responses?.pic_name || '—'} />
            <KV k="Submitted on" v={fmtT(q.submitted_at)} />
          </Card>
        )}

        <Card id="jp-quote" title="Signed proposal / quotation" actions={q.quote_ref && (
          q.signature_token
            ? <button className="sm" onClick={() => window.open(`${window.location.origin}/sign/${q.signature_token}`, '_blank', 'noopener')}>View / print</button>
            : <button className="sm" onClick={() => printQuotation(q, d.summary?.client)}>View / print</button>
        )}>
          {attachments.length > 0 && (
            <div style={{ marginBottom: q.quote_ref ? 14 : 0 }}>
              {attachments.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
                  <span style={{ flex: 1, fontSize: 13 }}><b>{a.label}</b>{a.file_name ? <span style={{ color: 'var(--text3)' }}> · {a.file_name}</span> : ''}</span>
                  <button className="sm primary" onClick={() => openFile('job_file', a.id, false)}>View</button>
                  <button className="sm" onClick={() => openFile('job_file', a.id, true)}>Download</button>
                </div>
              ))}
            </div>
          )}
          {q.quote_ref ? <>
            <KV k="Quotation ref" v={q.quote_ref} /><KV k="Status" v={q.status} /><KV k="CT17 man-days" v={q.mandays} />
            <KV k="Quoted fee" v={q.fee_ex_gst != null ? `${money(q.fee_ex_gst)} excl. GST · ${money(q.fee_inc_gst)} incl. 9% GST` : '—'} />
            <KV k="Signed" v={q.signed_date ? `${fmt(q.signed_date)} · ${q.signatory_name || ''} ${q.signatory_designation ? '(' + q.signatory_designation + ')' : ''}` : '—'} />
            <KV k="Accepted" v={q.application_accepted_by ? `${q.application_accepted_by} · ${fmtT(q.application_accepted_at)}` : (q.signed_via_token ? 'Signed via secure link' : '—')} />
          </> : (attachments.length === 0 && <Empty>No quotation on record.</Empty>)}
        </Card>

        {/* One line, one button. "View audit plan" opens the CLIENT'S OWN acknowledgement page (/ack/<token>),
            which renders the full audit plan plus their signed acknowledgement certificate. An acknowledged
            job shows that page read-only — there is no form for the assessor to submit. */}
        <Card id="jp-planack" title="Audit plan">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>Audit plan sent on <b>{d.planning?.plan_sent_at ? fmtT(d.planning.plan_sent_at) : '—'}</b></span>
            {d.planning?.client_ack_token && (
              <button className="sm" style={{ marginLeft: 'auto' }}
                onClick={() => window.open(`${window.location.origin}/ack/${d.planning.client_ack_token}`, '_blank', 'noopener')}>
                View audit plan
              </button>
            )}
          </div>
        </Card>

        {/* One row per auditor: name · submitted · button. "View COI declaration" opens that auditor's OWN
            declaration page (/coi/<their token>), which renders their signed certificate read-only once
            submitted — the assessor sees the real document, not a rendering of its answers. */}
        <Card id="jp-coi" title="Auditor COI / impartiality declaration">
          {(d.coi || []).length === 0 ? <Empty>No COI declarations on record.</Empty> : (d.coi || []).map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              marginTop: i ? 8 : 0, borderTop: i ? '0.5px solid var(--border)' : 'none', paddingTop: i ? 8 : 0 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{c.auditor || c.slot || '—'}</span>
              {c.flagged && <span className="badge badge-danger">flagged / high-risk</span>}
              <span style={{ fontSize: 12.5, color: 'var(--text2)' }}>Submitted {fmtT(c.submitted_at)}</span>
              {c.token && (
                <button className="sm" style={{ marginLeft: 'auto' }}
                  onClick={() => window.open(`${window.location.origin}/coi/${c.token}`, '_blank', 'noopener')}>
                  View COI declaration
                </button>
              )}
            </div>
          ))}
        </Card>

        <Card id="jp-meet" title="Opening & closing meeting attendance">
          {parts.length > 0 ? (
            <div className="table-wrap"><table>
              <thead><tr><th>Name</th><th>Designation</th><th>Opening</th><th>Closing</th></tr></thead>
              <tbody>{parts.map((p, i) => <tr key={i}><td>{p.name || '—'}</td><td>{p.designation || '—'}</td><td>{p.opening ? '✓' : ''}</td><td>{p.closing ? '✓' : ''}</td></tr>)}</tbody>
            </table></div>
          ) : <Empty>No meeting attendees recorded separately (see report).</Empty>}
        </Card>

        <Card id="jp-exec" title="Audit report review & Approval">
          <KV k="Execution mode" v={d.execution?.execution_mode || 'System'} />
          <KV k="Office review by" v={d.office_review?.reviewed_by ? `${d.office_review.reviewed_by} · ${fmtT(d.office_review?.reviewed_at)}` : '—'} />
          <KV k="Invoice cleared" v={d.office_review?.invoice_cleared ? 'Yes' : 'No'} />
          <KV k="Decision outcome" v={dec.outcome} />
          <KV k="Decision by" v={dec.decision_by ? `${dec.decision_by}${dec.decision_role ? ` (${dec.decision_role})` : ''} · ${fmt(dec.decided_at)}` : '—'} />
          <KV k="Report revisions" v={(d.revision_history || []).map(r => `v${r.version}`).join(', ') || '—'} />
          {d.return_reasons?.last_return_reason && <KV k="Last return reason" v={d.return_reasons.last_return_reason} />}
        </Card>

        <Card id="jp-endorse" title="Client report endorsement">
          {re ? <>
            <KV k="Endorsed by" v={re.name} /><KV k="Designation" v={re.designation} /><KV k="Company" v={re.company} />
            <KV k="Report version" v={re.report_version ? `v${re.report_version}` : '—'} /><KV k="Endorsed at" v={fmtT(re.submitted_at)} />
            <KV k="Access email" v={re.access_email} /><KV k="IP / device" v={re.ip ? `${re.ip}${re.user_agent ? ' · ' + re.user_agent.slice(0, 40) : ''}` : '—'} />
          </> : <Empty>Not yet endorsed.</Empty>}
        </Card>

        <Card id="jp-wshc" title="WSHC submission & finalisation">
          <KV k="Submitted to WSHC" v={d.wshc?.submitted_by ? `${d.wshc.submitted_by} · ${fmt(d.wshc.submitted_at)}` : '—'} />
          <KV k="WSHC outcome" v={d.wshc?.outcome} /><KV k="New bizSAFE expiry" v={fmt(d.wshc?.new_expiry)} />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>The WSHC online application is performed externally; AuditPM records the submission only.</div>
        </Card>

        <Card title="Internal attendance evidence">
          <div style={{ fontSize: 11.5, color: 'var(--text2)', marginBottom: 8 }}>{sa.label || 'Internal attendance evidence — not part of the client report.'}</div>
          {sa.has_photo
            ? <button className="sm primary" onClick={() => openFile('evidence', sa.evidence_id, false)}>View session photo</button>
            : <div style={{ fontSize: 12.5 }}><b>No session photo — auditor reason:</b> {sa.reason || '—'}</div>}
        </Card>

        {implPhotos.length > 0 && (
          <Card title="Implementation photos / RCM photos">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {implPhotos.map(e => (
                <div key={e.evidence_id} style={{ width: 150 }}>
                  {mediaById[e.evidence_id]
                    ? <img src={mediaById[e.evidence_id]} alt={e.file_name || 'photo'} style={{ width: '100%', borderRadius: 6, border: '0.5px solid var(--border)', cursor: 'pointer', display: 'block' }} onClick={() => openFile('evidence', e.evidence_id, false)} />
                    : <div style={{ width: '100%', height: 100, background: 'var(--bg2)', borderRadius: 6 }} />}
                  <div style={{ fontSize: 10.5, color: 'var(--text3)', margin: '2px 0', wordBreak: 'break-all' }}>{e.file_name}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card id="jp-evidence" title="Evidence & report files">
          {otherFiles.length === 0 ? <Empty>No evidence files.</Empty> : otherFiles.map(e => (
            <div key={e.evidence_id} style={{ fontSize: 12.5, padding: '5px 0', borderBottom: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span>{labelEvidence(e.type)} — {e.title || e.file_name || 'file'}</span>
              <span style={{ whiteSpace: 'nowrap' }}><button className="sm" onClick={() => openFile('evidence', e.evidence_id, false)}>View</button> <button className="sm" onClick={() => openFile('evidence', e.evidence_id, true)}>Download</button></span>
            </div>
          ))}
        </Card>

        {hasReport && (
          <Card id="jp-report" title={`Audit report (Rev ${reportDoc.current_version || reportDoc.snapshot?.version || ''})`}>
            <ReportPackageBlock status={reportDoc.package_status} jobId={job.job_id} openFile={openFile} />
          </Card>
        )}

        <Card id="jp-trail" title="Job audit trail (curated)">
          {(d.audit_trail || []).length === 0 ? <Empty>No entries.</Empty> : (d.audit_trail || []).map((t, i) => (
            <div key={i} style={{ fontSize: 11.5, padding: '2px 0', color: 'var(--text2)' }}>{fmtT(t.at)} — <b style={{ color: 'var(--text)' }}>{t.action}</b>{t.by ? ` · ${t.by} (${t.role || ''})` : ''}{t.label ? ` · ${t.label}` : ''}</div>
          ))}
        </Card>
      </div>
    </div>
  )
}

// ── Official report package — the SAME polished, letterhead PDF the client endorses (rendered via headless
// Chromium and stored once, server-side). The assessor is shown ONLY this, never a live in-browser
// reconstruction of the report data: a re-render can drift from the document actually issued to the client
// (spacing, pagination, exactly what evidence pages were bundled), so showing it would let an assessor form a
// judgment on something other than the real record. Mirrors the same policy already used internally in
// JobDetail.js (office "View report") and on the client's own acknowledgement page.
function ReportPackageBlock({ status, jobId, openFile }) {
  if (status === 'ready') return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button className="sm primary" onClick={() => openFile('report_package', jobId, false)}>View official report package</button>
      <button className="sm" onClick={() => openFile('report_package', jobId, true)}>Download</button>
    </div>
  )
  const msg = status === 'preparing' ? 'The official report package is being prepared — check back shortly.'
    : status === 'error' ? 'The official report package could not be generated. Please contact CCIS.'
    : 'The official report package is not yet available for this job.'
  return <div className="notice notice-warn" style={{ marginBottom: 0 }}>{msg}</div>
}

// ── ConSASS/SHMS sample pack: one company's nine records, opened from the index table ───────────────
// Mirrors the WSH RM Audit "Open" pattern so the index stays a clean one-line-per-company list.
function SamplePack({ sample, onBack, openFile }) {
  const byType = {}; (sample.files || []).forEach(f => { if (f.doc_type !== 'other') byType[f.doc_type] = f })
  const others = sampleOthers(sample)
  const dateStr = sampleDateStr(sample)
  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <button className="sm" style={{ marginBottom: 12 }} onClick={onBack}>← Back to assessment</button>
      <Card title={sample.company_name}>
        <KV k="Project" v={sample.project_name || '—'} />
        <KV k="Audit date" v={dateStr || '—'} />
        <KV k="Records provided" v={`${sampleProvided(sample)} of ${CONSASS_DOC_TYPES.length}`} />
        {others.length > 0 && <KV k="Other documents" v={others.length} />}
      </Card>
      <Card title="Audit records">
        <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 12 }}>
          Click <b>View</b> to open a document; items marked “Not provided” were not part of this sample.
        </div>
        <div className="table-wrap"><table>
          <tbody>
            {CONSASS_DOC_TYPES.map(([key, label], i) => {
              const f = byType[key]
              return (
                <tr key={key}>
                  <td style={{ width: 24, fontSize: 12, color: 'var(--text3)' }}>{i + 1}</td>
                  <td style={{ fontSize: 13, fontWeight: 500 }}>{label}</td>
                  <td style={{ width: 170, textAlign: 'right' }}>
                    {f
                      ? <span style={{ whiteSpace: 'nowrap' }}>
                          <button className="sm primary" onClick={() => openFile('assessment_file', f.id, false)}>View</button>{' '}
                          <button className="sm" onClick={() => openFile('assessment_file', f.id, true)}>Download</button>
                        </span>
                      : <span style={{ fontSize: 12, color: 'var(--text3)' }}>Not provided</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table></div>
      </Card>
      {others.length > 0 && (
        <Card title={`Other documents (${others.length})`}>
          <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 12 }}>
            Additional records released for this audit beyond the nine above.
          </div>
          <div className="table-wrap"><table>
            <tbody>
              {others.map((f, i) => (
                <tr key={f.id}>
                  <td style={{ width: 24, fontSize: 12, color: 'var(--text3)' }}>{i + 1}</td>
                  <td style={{ fontSize: 13, fontWeight: 500 }}>{f.label || f.file_name || 'Untitled document'}</td>
                  <td style={{ width: 170, textAlign: 'right' }}>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <button className="sm primary" onClick={() => openFile('assessment_file', f.id, false)}>View</button>{' '}
                      <button className="sm" onClick={() => openFile('assessment_file', f.id, true)}>Download</button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>
      )}
    </div>
  )
}
